import React, { useState } from 'react';
import { Transfer, Input, Space, Avatar, Typography, Tag, Empty, Spin } from 'antd';
import { SearchOutlined, UserOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import { useAvailableDoctors, usePartnerDoctors } from '../../hooks';

const { Text } = Typography;

const DoctorAssociation = ({ partnerId, onAssociate }) => {
  const [targetKeys, setTargetKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const { data: associatedData, isLoading: associatedLoading } = usePartnerDoctors(partnerId);
  const { data: availableData, isLoading: availableLoading } = useAvailableDoctors(partnerId);

  const associatedDoctors = associatedData?.data || [];
  const availableDoctors = availableData?.data || [];

  const allDoctors = [...availableDoctors, ...associatedDoctors];

  const dataSource = allDoctors.map((doctor) => ({
    key: doctor.id.toString(),
    doctor,
    disabled: associatedDoctors.some((d) => d.id === doctor.id),
  }));

  const filterOption = (inputValue, option) => {
    const doctor = option.doctor;
    const searchLower = inputValue.toLowerCase();

    return (
      doctor.name?.toLowerCase().includes(searchLower) ||
      doctor.first_name?.toLowerCase().includes(searchLower) ||
      doctor.last_name?.toLowerCase().includes(searchLower) ||
      doctor.email?.toLowerCase().includes(searchLower) ||
      doctor.category_name?.toLowerCase().includes(searchLower)
    );
  };

  const handleChange = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys);
  };

  const handleSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const handleSearch = (dir, value) => {
    setSearchValue(value);
  };

  const renderItem = (item) => {
    const doctor = item.doctor;
    return {
      label: (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Avatar
              size="small"
              src={doctor.avatar}
              icon={!doctor.avatar && <UserOutlined />}
              style={{ backgroundColor: !doctor.avatar ? '#52c41a' : 'transparent' }}
            />
            <div>
              <Text strong style={{ display: 'block', fontSize: 13 }}>
                {doctor.name || `${doctor.first_name} ${doctor.last_name}`}
              </Text>
              {doctor.category_name && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <MedicineBoxOutlined style={{ marginRight: 4 }} />
                  {doctor.category_name}
                </Text>
              )}
            </div>
          </Space>
          {doctor.consultations_count !== undefined && (
            <Tag color="blue" style={{ fontSize: 11 }}>
              {doctor.consultations_count}
            </Tag>
          )}
        </Space>
      ),
      value: item.key,
      disabled: item.disabled,
    };
  };

  if (associatedLoading || availableLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" tip="Loading doctors..." />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search doctors by name, email, or specialty"
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          allowClear
          style={{ marginBottom: 16 }}
        />
      </div>

      <Transfer
        dataSource={dataSource}
        titles={['Available Doctors', 'Doctors to Associate']}
        targetKeys={targetKeys}
        selectedKeys={selectedKeys}
        onChange={handleChange}
        onSelectChange={handleSelectChange}
        onSearch={handleSearch}
        filterOption={filterOption}
        render={renderItem}
        listStyle={{
          width: '100%',
          height: 500,
        }}
        style={{ width: '100%' }}
        showSearch
        locale={{
          itemUnit: 'doctor',
          itemsUnit: 'doctors',
          searchPlaceholder: 'Search',
          notFoundContent: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No doctors found"
            />
          ),
        }}
        operations={['Add', 'Remove']}
        oneWay={false}
      />
    </div>
  );
};

export default DoctorAssociation;